import { computed, inject, Injectable, signal } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import firebase from 'firebase/compat/app';
import { catchError, of } from 'rxjs';
import { SetAuthToken, SetUser, UnsetUser } from '../../store/user/user.action';
import { AuthStateModel, UserModel } from '../../store/user/user.state';
import { UtilitiesService } from '../utilities.service';
import { ApiHttpService } from './api-http.service';
import { FsAuthService } from './fs-auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  userData: any;
  loggedIn = false;
  // private firebaseAuth = inject(Auth);
  afAuth = inject(AngularFireAuth);
  router = inject(Router);
  utils = inject(UtilitiesService);
  private api = inject(ApiHttpService);
  private fsAuthService = inject(FsAuthService);

  private store = inject(Store);
  private authSignal = signal(this.store.selectSnapshot((state) => state.auth));
  private userSignal = signal(this.store.selectSnapshot((state) => state.user));

  authToken = computed(() => this.authSignal().authToken);
  userDataComputed = computed(() => this.userSignal().user);
  isLoggedIn2 = computed(
    () => !!this.authSignal()?.user && !!this.authSignal()?.authToken
  );

  constructor() {
    // Mantener sincronizado estado Firebase ↔ NGXS
    // const auth = getAuth();
    // onAuthStateChanged(auth, (user) => {
    //   if (!user) {
    //     this.store.dispatch(new UnsetUser());
    //   }
    // });
  } // private api: ApiHttpService // private store: Store, // public utils: UtilitiesService, // public router: Router, // public afAuth: AngularFireAuth,

  setUser(result: any): Promise<any> {
    const user = result.user.multiFactor.user;
    const payload: UserModel = {
      id: user.uid,
      email: user.email,
      name: user.displayName,
      photo: user.photoURL,
      phone: user.phoneNumber,
      provider: result.additionalUserInfo.providerId,
    };

    // TODO: Check authentication new endpoint
    // this.fsAuthService.getUsersMe().subscribe((res: any) => {
    //   console.log({ res });
    // });

    return new Promise((resolve, reject) => {
      this.api
        .post('user', payload)
        .pipe(
          catchError((error) => {
            reject(error.error);
            return of(null); // Return a fallback observable to avoid breaking the observable chain
          })
        )
        .subscribe((response: any) => {
          if (response?.success) {
            this.store.dispatch(new SetUser(payload));
            this.store.dispatch(new SetAuthToken(user.accessToken));
          }
          resolve(response);
        });
    });
  }

  handleSignIn(type: string, email: string, password: string) {
    let provider;
    if (type === 'GOOGLE') {
      provider = new firebase.auth.GoogleAuthProvider();
      return this.afAuth.signInWithPopup(provider);
    } else if (type === 'APPLE') {
      provider = new firebase.auth.OAuthProvider('apple.com');
      return firebase.auth().signInWithPopup(provider);
    } else if (type === 'FACEBOOK') {
      provider = new firebase.auth.FacebookAuthProvider();
      return this.afAuth.signInWithPopup(provider);
    } else {
      return this.afAuth.signInWithEmailAndPassword(email, password);
    }
  }

  async register(email: string, password: string) {
    try {
      const result: any = await this.afAuth.createUserWithEmailAndPassword(
        email,
        password
      );
      const userResponse = await this.setUser(result);
      return userResponse;
    } catch (error: any) {
      let errorMessage =
        this.errorMsg(error) ??
        error?.error ??
        error?.message ??
        'An unknown error occurred';

      return { success: false, message: errorMessage };
    }
  }

  async signIn(type: string, email: string, password: string) {
    try {
      const result: any = await this.handleSignIn(type, email, password);
      const userResponse = await this.setUser(result);

      // TODO: add new endpoint user me

      return userResponse;
    } catch (error: any) {
      let errorMessage =
        this.errorMsg(error) ??
        error?.error ??
        error?.message ??
        'An unknown error occurred';

      return { success: false, message: errorMessage };
    }
  }

  async sendEmail(email: string) {
    try {
      await this.afAuth.sendPasswordResetEmail(email);
      return {
        success: true,
        message: 'An email to reset your password has been sent.',
      };
    } catch (error: any) {
      let errorMessage =
        this.errorMsg(error) ??
        error?.error ??
        error?.message ??
        'An unknown error occurred';
      return { success: false, message: errorMessage };
    }
  }

  signOut() {
    // this.store.dispatch(new UnsetUser()).subscribe({
    //   next: () => {
    //     this.afAuth.signOut();
    //   },
    //   error: (error) => {
    //     throw new Error(error);
    //   },
    // });

    try {
      this.afAuth.signOut().then((callback) => {
        // console.log('Sesion fin');
        // console.log({ callback });
      });
      this.store
        .dispatch(new UnsetUser())
        .toPromise()
        .then(() => {});
      // this.router.navigate(['/login']); // opcional
    } catch (error) {
      console.error('Error al cerrar sesión', error);
    }

    window.location.reload();
  }

  // Handle Firebase-specific error messages for authentication and email actions
  errorMsg(error: any): string {
    let errorMessage = '';
    if (error.code === 'auth/email-already-in-use') {
      errorMessage = 'This email is already in use.';
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email format.';
    } else if (error.code === 'auth/weak-password') {
      errorMessage = 'Password is too weak.';
    } else if (error.code === 'auth/user-not-found') {
      errorMessage =
        'No account found with this email. Please check the email.';
    } else if (error.code === 'auth/wrong-password') {
      errorMessage = 'Incorrect password. Please try again.';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Too many login attempts. Please try again later.';
    } else if (error.code === 'auth/network-request-failed') {
      errorMessage = 'Network error. Please check your internet connection.';
    } else if (error.code === 'auth/invalid-action-code') {
      errorMessage =
        'Invalid or expired reset password link. Please try requesting a new one.';
    } else if (error.code === 'auth/user-disabled') {
      errorMessage = 'This account has been disabled. Please contact support.';
    } else if (error.code === 'auth/cannot-send-email') {
      errorMessage =
        'There was an issue sending the email. Please try again later.';
    } else if (error.code === 'auth/requires-recent-login') {
      errorMessage = 'Please log in again to complete this action.';
    }

    return errorMessage;
  }

  get isLoggedIn(): boolean {
    return !!this.store.selectSnapshot(
      (state) => state.auth.user && state.auth.authToken
    );
  }

  get userInfo(): AuthStateModel {
    const { user, authToken } = this.store.selectSnapshot(
      (state) => state.auth
    );
    return { user, authToken };
  }

  // getToken$() {
  //   const user = this.firebaseAuth.currentUser;

  //   if (!user) {
  //     return from(Promise.resolve(null));
  //   }

  //   return from(getIdToken(user, true)); // `true` fuerza actualización si está expirado
  // }
}
